import { ActionTree } from 'vuex'
import { ModuleNamespaces, RootState } from '@/store/types/storeGlobalTypes'
import {
  BankdataIdEnum,
  facturaEMutationTypes,
  IBankdata,
  IBankdraftForUpdate,
  IFacturaEForUpdate,
  IFacturaEState,
  IProfessional,
  paymentTypeWithBankdata
} from '@/store/modules/facturaE/facturaETypes'
import { FacturaEService } from '@/services/FacturaEService'
import { AlertsTypes, ComponentWhereIsRendered } from '@/store/modules/alerts/alertsTypes'
import { i18n } from '@/plugins/vue-i18n'
import { DialogTypes } from '@/store/modules/dialog/dialogTypes'

export const actions: ActionTree<IFacturaEState, RootState> = {
  async fetchBankdata({ commit, state }): Promise<void> {
    const invoiceId = state.invoiceId
    const bankdataType = paymentTypeWithBankdata[String(state.paymentType) as BankdataIdEnum]
    if (!invoiceId || bankdataType === undefined) {
      commit(facturaEMutationTypes.SET_BANKDATA, [])
      return
    }

    try {
      const { data } = await new FacturaEService().bankdata(invoiceId, bankdataType)
      commit(facturaEMutationTypes.SET_BANKDATA, data as IBankdata[])
    } catch (error) {
      commit(facturaEMutationTypes.SET_BANKDATA, [])
    }
  },
  async fetchProfessionalList({ commit }): Promise<IProfessional[]> {
    try {
      const { data } = await new FacturaEService().professionals()
      commit(facturaEMutationTypes.SET_PROFESSIONAL_LIST, data as IProfessional[])
      return data as IProfessional[]
    } catch (error) {
      commit(facturaEMutationTypes.RESET_PROFESSIONAL)
      return []
    }
  },
  setSelectedProfessional({ commit, state }, professionalId: string | null) {
    commit(facturaEMutationTypes.SET_SELECTED_PROFESSIONAL,
      state.professionalList.find(professional => professional.id === professionalId)
    )
  },
  setInvoiceId({ commit }, invoiceId: string | null) {
    commit(facturaEMutationTypes.SET_INVOICE_ID, invoiceId)
    commit(facturaEMutationTypes.SET_PROFESSIONAL_LIST, [])
    commit(facturaEMutationTypes.SET_BANKDATA, [])
    commit(facturaEMutationTypes.SET_PAYMENT_TYPE, undefined)
  },
  setPaymentType({ commit }, paymentType: string | null) {
    commit(facturaEMutationTypes.SET_PAYMENT_TYPE, paymentType)
  },
  async updateFacturaE({ dispatch }, facturaE: IFacturaEForUpdate): Promise<boolean> {
    try {
      await new FacturaEService().updateFacturaE(facturaE)
      await dispatch(
        `${ ModuleNamespaces.ALERTS }/showAlert`,
        {
          type: AlertsTypes.SUCCESS,
          message: i18n.t('components.alerts.register_save'),
          componentWhereIsRendered: ComponentWhereIsRendered.MAIN
        },
        { root: true }
      )
      return true
    } catch (error) {
      await dispatch(
        `${ ModuleNamespaces.DIALOG }/showDialog`,
        {
          type: DialogTypes.ERROR,
          message: getExceptionResponseMessage(error, 'components.alerts.register_error')
        },
        { root: true }
      )
      await dispatch(`${ ModuleNamespaces.ERROR }/existsEndpointError`, {}, { root: true })
      return false
    }
  },
  async updateFacturaEBankdraft({ dispatch }, facturaEBankdraft: IBankdraftForUpdate): Promise<boolean> {
    try {
      await new FacturaEService().updateBankdraft(facturaEBankdraft)
      await dispatch(
        `${ ModuleNamespaces.ALERTS }/showAlert`,
        {
          type: AlertsTypes.SUCCESS,
          message: i18n.t('components.alerts.register_save'),
          componentWhereIsRendered: ComponentWhereIsRendered.MAIN
        },
        { root: true }
      )
      return true
    } catch (error) {
      await dispatch(
        `${ ModuleNamespaces.DIALOG }/showDialog`,
        {
          type: DialogTypes.ERROR,
          message: getExceptionResponseMessage(error, 'components.alerts.register_error')
        },
        { root: true }
      )
      await dispatch(`${ ModuleNamespaces.ERROR }/existsEndpointError`, {}, { root: true })
      return false
    }
  }
}

function getExceptionResponseMessage(error: any, defaultI18nMsg: string) {
  try {
    try {
      return JSON.parse(error.response.data.exceptionDesc).exceptionDesc
    } catch (e) {
      return error.response.data.exceptionDesc
    }
  } catch (e) {
    return i18n.t(defaultI18nMsg)
  }
}
